import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { AuthFloatingField } from "@/components/auth";
import { TermsDocumentDialog } from "@/components/legal/TermsDocumentDialog";
import { NO_TERMS_DIALOG } from "@/constants/legal";
import { useCurrentTerms } from "@/hooks/useLegalDocuments";
import { OnboardingAvatarEditor } from "./OnboardingAvatarEditor";

interface OnboardingProfileStepProps {
  firstName: string;
  lastName: string;
  orgName: string;
  avatarPreviewUrl: string | null;
  error: string | null;
  isSubmitting: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onOrgNameChange: (value: string) => void;
  onAvatarFileSelected: (file: File) => void;
  onContinue: () => void;
  /** Whether the clickwrap is ticked. Unticked on every arrival — never pre-accepted. */
  termsAccepted: boolean;
  /** Reports both the tick and the version that was on screen when it happened. */
  onTermsAcceptedChange: (accepted: boolean, version: string | null) => void;
}

/**
 * OnboardingProfileStep — Step 1 of the onboarding wizard.
 *
 * Collects: avatar (optional), first name (required), last name (required),
 * organization name (required), and acceptance of the Platform Terms (required).
 *
 * The clickwrap is HERE rather than on the plan step because this is where the organization is
 * named, and the organization is the contracting party. Three properties make the agreement
 * enforceable rather than merely recorded: the box is unticked on every arrival, the document opens
 * in place at the exact version being accepted, and the version beside it comes from the server.
 * Opening the document is offered, not required — the client can tick the box straight away.
 */
export function OnboardingProfileStep({
  firstName,
  lastName,
  orgName,
  avatarPreviewUrl,
  error,
  isSubmitting,
  onFirstNameChange,
  onLastNameChange,
  onOrgNameChange,
  onAvatarFileSelected,
  onContinue,
  termsAccepted,
  onTermsAcceptedChange,
}: OnboardingProfileStepProps) {
  // The version the server is serving right now. Fetched here, on the screen that shows it, so the
  // wording and the recorded version are the same string from the same response.
  const { data: currentTerms, isLoading: termsLoading } = useCurrentTerms();
  const [noTermsOpen, setNoTermsOpen] = useState(false);
  const [documentOpen, setDocumentOpen] = useState(false);

  // Nothing published, or the lookup failed. Both mean the same thing to the client: there is no
  // document to agree to, so signup cannot proceed. `termsLoading` is excluded so the checkbox is
  // merely disabled while the request is in flight rather than accused of being unavailable.
  const termsAvailable = !termsLoading && currentTerms != null;
  const initial = firstName.trim().charAt(0).toUpperCase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onContinue();
  };

  return (
    <div className="gfg-ob-panel-in">
      <h1 className="gfg-auth-title">Set up your account</h1>
      <p className="gfg-auth-sub">Tell us a bit about you and your organization.</p>

      <OnboardingAvatarEditor
        initial={initial}
        previewUrl={avatarPreviewUrl}
        onFileSelected={onAvatarFileSelected}
      />

      <form onSubmit={handleSubmit}>
        <div className="gfg-field-row">
          <AuthFloatingField
            label="First name"
            type="text"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            autoFocus
          />
          <AuthFloatingField
            label="Last name"
            type="text"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
          />
        </div>

        <AuthFloatingField
          label="Organization name"
          type="text"
          value={orgName}
          onChange={(e) => onOrgNameChange(e.target.value)}
        />

        {error && (
          <p className="gfg-auth-hint" style={{ color: "var(--color-good-wine)", marginTop: 8 }}>
            {error}
          </p>
        )}

        {/* Unticked on every arrival: acceptance is an act the client performs now, not a state
            restored for them. Continue stays disabled until they do — and validateProfile checks it
            again, because a disabled button is a convenience and not a gate. */}
        <label className="gfg-terms-line">
          <input
            type="checkbox"
            checked={termsAccepted}
            disabled={!termsAvailable}
            onChange={(e) => onTermsAcceptedChange(e.target.checked, currentTerms?.version ?? null)}
          />
          <span>
            I agree to the{" "}
            {/* Opens IN PLACE rather than in a new tab: being ejected from signup to a browser PDF
                viewer, then having to find the way back, is the part that felt wrong. With nothing
                published there is no document to open, so it explains that instead of 404ing — and
                names who can fix it, because the client cannot. */}
            <a
              href={currentTerms?.url ?? "#"}
              onClick={(e) => {
                e.preventDefault();
                if (!termsAvailable) return setNoTermsOpen(true);
                setDocumentOpen(true);
              }}
            >
              Terms &amp; Conditions
            </a>
            {currentTerms && <span className="gfg-terms-version"> ({currentTerms.version})</span>}.
          </span>
        </label>

        <button
          type="submit"
          className="gfg-auth-btn"
          disabled={isSubmitting || !termsAccepted || !termsAvailable}
        >
          {isSubmitting ? "Saving…" : "Continue"}
        </button>
      </form>

      {currentTerms && (
        <TermsDocumentDialog
          open={documentOpen}
          url={currentTerms.url}
          version={currentTerms.version}
          onClose={() => setDocumentOpen(false)}
        />
      )}

      <Dialog open={noTermsOpen} onClose={() => setNoTermsOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 20 }}>
          {NO_TERMS_DIALOG.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.55 }}>{NO_TERMS_DIALOG.body}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoTermsOpen(false)} sx={{ color: "var(--color-rodeo-black)" }}>
            {NO_TERMS_DIALOG.dismiss}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
